/**
 * Features Component - Key features showcase
 */

export default function Features() {
    const features = [
        {
            title: 'Instant Copilot Readiness Assessment',
            description: 'Scan every page in your SharePoint site in minutes. Get a Readiness Score, Flesch Readability score, Structure Score and an 8-point quality report, all computed in your browser, no data leaving your tenant.',
            icon: (
                <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
            ),
            gradient: 'from-yellow-400 to-orange-500',
        },
        {
            title: 'Zero-Trust, Tenant-Scoped Processing',
            description: 'Analysis is scoped to your Microsoft 365 tenant so intranet content remains inside your environment and never leaves your tenant.',
            icon: (
                <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
            ),
            gradient: 'from-green-400 to-emerald-500',
        },
        {
            title: 'Actionable, Data-Driven Insights',
            description: 'Move from detection to resolution with precise recommendations that elevate employee engagement.',
            icon: (
                <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            ),
            gradient: 'from-blue-400 to-indigo-500',
        },
        {
            title: '8-Point Content Quality Report',
            description: 'Every page is checked against 8 quality signals: write for people first, avoid complex layouts, short sections, clear H2/H3 headings, metadata & intent, image alt text, strong page summary, and no keyword stuffing (TTR + N-gram analysis).',
            icon: (
                <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
            ),
            gradient: 'from-purple-400 to-pink-500',
        },
        {
            title: 'Professional Export & Sharing',
            description: 'Produce polished PDF reports ready for stakeholders to enable confident, data-driven decisions.',
            icon: (
                <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                </svg>
            ),
            gradient: 'from-cyan-400 to-blue-500',
        },
        {
            title: 'Intent Classification',
            description: 'Every page is automatically categorised (Safety, Compliance, Procedural, System, Factual or General) using a two-pass keyword scan that weights the page title 3× to reflect author intent.',
            icon: (
                <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
            ),
            gradient: 'from-red-400 to-rose-500',
        },
    ];

    return (
        <div className="py-24 bg-white" id="how-it-works">
            <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl sm:text-5xl font-black text-gray-900 mb-4">
                            Everything You Need to <span className="bg-gradient-to-r from-[#2ba6bb] to-[#16a8c5] bg-clip-text text-transparent">Optimize</span> Your Intranet
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto font-medium">
                            Comprehensive analysis tools designed to help you create a healthier, more engaging workplace.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {features.map((feature, index) => (
                            <div
                                key={index}
                                className="group relative bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-gray-100"
                            >
                                <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${feature.gradient} opacity-10 rounded-bl-full`}></div>
                                <div className="relative">
                                    <div className={`inline-flex items-center justify-center h-16 w-16 rounded-xl bg-gradient-to-br ${feature.gradient} text-white mb-6 shadow-lg group-hover:scale-110 transition-transform`}>
                                        {feature.icon}
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                                        {feature.title}
                                    </h3>
                                    <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
